import type { ProjectTime } from "../components/Timing";
import type { UserDetails } from "./UserDetails";

export interface EntryRequest {
	kase: number;
	subject: string;
	type: string;
	content: string;
	creator?: number;
	entryAt: string;
	createdAt: string;
}

export interface EntryResponse {
	id: number;
	kase?: {
		id: number;
		name: string;
		description: string;
		status: string;
		party?: {
			id: number;
			name: string;
		};
	};
	subject: string;
	type: string;
	content: string;
	creator?: {
		id: number;
		name: string;
	};
	activityType?: {
		id: number;
		name: string;
	};
	entryAt: string;
	createdAt: string;
}

export interface EntryForDisplay {
	id: number;
	project_id: number;
	project_ref: string;
	date: string;
	time: string;
	consultant: string;
	activity: string;
	details: string;
	requisitioner?: string;
	ispTag?: string;
	minutes: number;
}

export interface EntryForHistory {
	PartyID: number; // party.id
	PartyName: string; // party.name
	ProjectID: number; // kase.id
	ProjectRef: string; // kase.name
	ProjectDesc: string; // kase.description
	ProjectStatus: string; // kase.status
	CreatorID: number; // creator.id
	CreatorName: string; // creator.name
	CreatedAt: string; // createdAt
	Content: string; // content
}

export interface EntryForHistoryMap {
	[creatorName: string]: EntryForHistory[];
}

export function EntryRequestFromProjectTime(
	projectID: string,
	t: ProjectTime,
	u: UserDetails,
): EntryRequest {
	const isoDate = new Date(t.work_date).toISOString();
	let ispTag: string | undefined;

	if (projectID.endsWith("ISP")) {
		ispTag = newISPTag();
	}

	return {
		kase: t.link_id,
		subject: "Time Tracking",
		type: "note",
		content: MarshalContent({
			activity: t.activity,
			minutes: CalculateMinutesFromProjectTime(t).toFixed(0),
			details: t.work_desc,
			requisitioner: t.requisitioner,
			ispTag: ispTag,
		}),
		creator: u.id,
		entryAt: isoDate,
		createdAt: isoDate,
	};
}

export function CalculateMinutesFromProjectTime(t: ProjectTime) {
	// 60s = 60000ms: 60000 / 1000 = 60 => 60 / 60 = 1minute
	let elapsedMinutes = Math.trunc(t.run_time / 1000 / 60);

	if (t.override_hours != null) {
		elapsedMinutes += Math.trunc(t.override_hours * 60);
	}

	if (t.override_minutes != null) {
		elapsedMinutes += Math.trunc(t.override_minutes);
	}

	if (!t.override_minimum && elapsedMinutes < 15) {
		elapsedMinutes = 15;
	}

	return elapsedMinutes;
}

export function EntryRequestFromEntryDisplay(e: EntryForDisplay): EntryRequest {
	const isoDate = new Date(e.date).toISOString();

	return {
		kase: e.project_id,
		subject: "Time Tracking",
		type: "note",
		content: MarshalContent({
			activity: e.activity,
			minutes: e.minutes,
			details: e.details,
			requisitioner: e.requisitioner,
			ispTag: e.ispTag,
		}),
		creator: undefined, // We set this to undefined to avoid overwriting the original creator when updating an entry
		entryAt: isoDate,
		createdAt: isoDate,
	};
}

interface ContentProps {
	activity: string;
	minutes: number | string;
	details: string;
	requisitioner?: string;
	ispTag?: string;
}

function MarshalContent({
	activity,
	minutes,
	details,
	requisitioner,
	ispTag,
}: ContentProps): string {
	const req = requisitioner != null ? `\nRequisitioner: ${requisitioner}` : "";
	const isp = ispTag != null ? `\nISP Year: ${ispTag}` : "";
	return (
		"Time Tracking Entry" +
		"\nActivity: " +
		activity +
		"\nTime Worked: " +
		minutes +
		"\nDetails: " +
		details +
		// Optional Fields start here...
		req +
		isp
	);
}

export function UnMarshalContent(content: string): ContentProps | null {
	const c = content.split("\n");
	if (c.length < 4) {
		return null;
	}
	let activity = c[1];
	activity = activity.replace("Activity: ", "");
	let minutes = c[2];
	minutes = minutes.toString().replace("Time Worked: ", "");
	let details = c[3];
	details = details.replace("Details: ", "");
	const optionalFields = c.slice(4);
	let requisitioner = "";
	let isp = "";
	optionalFields.forEach((o) => {
		if (o.startsWith("Requisitioner: ")) {
			requisitioner = o.replace("Requisitioner: ", "");
		}
		if (o.startsWith("ISP Year: ")) {
			isp = o.replace("ISP Year: ", "");
		}
	});
	return {
		activity: activity,
		minutes: minutes,
		details: details,
		requisitioner: requisitioner,
		ispTag: isp,
	};
}

export function newISPTag() {
	return new Date().getFullYear().toFixed(0);
}
